import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { UTC_FORMAT, formatDate } from '../../../utils/luxon';
import { EntityType, getAllEntities } from '../../admin/entity/store';
import { Button } from '../../shared/button/Button';
import { SpaceBetweenButtonWrapper } from '../../shared/button/SpaceBetweenButtonWrapper';
import { DatePicker } from '../../shared/datepicker/DatePicker';
import { Dropdown, DropdownOption } from '../../shared/dropdown/Dropdown';
import { Document } from '../../shared/icons';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import styles from './DatasetInstance.module.scss';
import { DatasetModal } from './modal-instance/DatasetModal';
import { DatasetInstanceTimeline, DocumentMeta, getDocumentMetaHasUpdated, getIsUpdatingMetaDetails, toggleEditMetaDataModal, updateDocumentMetaDetailsStarted, updateDocumentMetaField } from './store';
import { DATASET_INSTANCE_HEADER, NAV_BAR, ANALYSIS_MARGIN, TIMELINE_WRAPPER, ANALYSIS_PADDING } from '../../../hooks/useSplitView';
import { Text } from '../../shared/text/Text';
import { DocumentType } from '../../documents/store/types';

interface EditDocumentMetaModalProps {
    documentMeta: DocumentMeta;
    timeline: DatasetInstanceTimeline;
}

export const EditDocumentMetaModal: React.FC<EditDocumentMetaModalProps> = ({ documentMeta, timeline }) => {
    const dispatch = useAppDispatch();
    const allEntities = useAppSelector(getAllEntities);
    const hasUpdated = useAppSelector(getDocumentMetaHasUpdated);
    const isUpdating = useAppSelector(getIsUpdatingMetaDetails);

    const closeModal = useCallback(() => dispatch(toggleEditMetaDataModal(null)), [dispatch]);
    const saveChanges = useCallback(() => dispatch(updateDocumentMetaDetailsStarted()), [dispatch]);

    const updateMetaField = useCallback((field: keyof DocumentMeta, value: string | number) => dispatch(updateDocumentMetaField(field, value)), [dispatch]);

    const { instances } = timeline;
    const { entityA, entityB, isAgency, executedDate, datedAsOfDate, documentDescription, documentType } = documentMeta;

    const agencyEntities = useMemo(() => allEntities.filter(({ canBeAgent }) => canBeAgent).map(({ name, entityId }) => ({ value: entityId!.toString(), label: name })), [allEntities]);
    const entityOptions = useMemo(() => {
        let entities = allEntities.filter(({ type }) => type === EntityType.COMPANY).map(({ name, entityId }) => ({ value: entityId!.toString(), label: name }));
        if (isAgency) {
            entities = entities.concat(agencyEntities);
        }
        return entities;
    }, [allEntities, isAgency, agencyEntities]);

    const uploadEntityA = useMemo(() => {
        if (!entityA) {
            return null;
        }
        return entityOptions.find(({ value }) => value === entityA.toString()) || null;
    }, [entityA, entityOptions]);

    const uploadEntityB = useMemo(() => {
        if (!entityB) {
            return null;
        }
        return entityOptions.find(({ value }) => value === entityB.toString()) || null;
    }, [entityB, entityOptions]);

    const entityIsAgency = useCallback((id: number) => allEntities.find(({ entityId }) => entityId === id)?.canBeAgent === 1 || false, [allEntities]);

    const entityAOptions = useMemo(() => {
        if (entityB) {
            if (isAgency && !entityIsAgency(entityB)) {
                return agencyEntities;
            }
            return entityOptions.filter(({ value }) => value !== entityB.toString());
        }
        return entityOptions;
    }, [entityOptions, isAgency, entityB, entityIsAgency, agencyEntities]);

    const entityBOptions = useMemo(() => {
        if (entityA) {
            if (isAgency && !entityIsAgency(entityA)) {
                return agencyEntities;
            }
            return entityOptions.filter(({ value }) => value !== entityA.toString());
        }
        return entityOptions;
    }, [entityOptions, isAgency, entityA, entityIsAgency, agencyEntities]);

    const dateValue = useMemo(() => new Date(executedDate), [executedDate]);
    const datedAsOfValue = useMemo(() => datedAsOfDate ? new Date(datedAsOfDate) : null, [datedAsOfDate]);

    const setExecutedDate = useCallback((value: Date | string | null) => {
        const executedDate = value ? formatDate(value, UTC_FORMAT) : null;
        if (!isNull(executedDate)) {
            updateMetaField('executedDate', executedDate);
        }
    }, [updateMetaField]);

    const setDatedAsOf = useCallback((value: Date | string | null) => {
        const datedAsOf = value ? formatDate(value, UTC_FORMAT) : null;
        if (!isNull(datedAsOf)) {
            updateMetaField('datedAsOfDate', datedAsOf);
        }
    }, [updateMetaField]);

    const legacyDocuments = useMemo(() => instances.filter(({ isLegacy }) => isLegacy), [instances]);

    const minDate = useMemo(() => {
        if (documentType === DocumentType.ORIGINAL) {
            if (legacyDocuments.length === 0) {
                return undefined;
            }
            return new Date(legacyDocuments.map(({ executedDate }) => executedDate).sort((a, b) => Date.parse(b) - Date.parse(a))[0]);
        }
        return new Date(instances.map(({ executedDate }) => executedDate).sort((a, b) => Date.parse(a) - Date.parse(b))[0]);
    }, [instances, legacyDocuments, documentType]);

    const minDatedAsOf = useMemo(() => {
        if (documentType === DocumentType.ORIGINAL) {
            if (legacyDocuments.length === 0) {
                return undefined;
            }
            return new Date(legacyDocuments.map(({ datedAsOfDate, executedDate }) => datedAsOfDate || executedDate).sort((a, b) => Date.parse(b) - Date.parse(a))[0]);
        }
        return new Date(instances.map(({ datedAsOfDate, executedDate }) => datedAsOfDate || executedDate).sort((a, b) => Date.parse(a) - Date.parse(b))[0]);
    }, [instances, legacyDocuments, documentType]);

    const maxDate = useMemo(() => {
        if (documentType !== DocumentType.ORIGINAL) {
            return undefined;
        }
        return new Date(instances.filter(({ isLegacy }) => !isLegacy).filter(({ documentType }) => documentType !== DocumentType.ORIGINAL).map(({ datedAsOfDate, executedDate }) => datedAsOfDate || executedDate).sort((a, b) => Date.parse(a) - Date.parse(b))[0]);
    }, [documentType, instances]);

    const leftButtons = useMemo(() => [<Button label='Close' onClick={closeModal} key='close' />], [closeModal]);
    const rightButtons = useMemo(() => [<Button label='Save' onClick={saveChanges} key='save' disabled={!hasUpdated || isUpdating} />], [hasUpdated, isUpdating, saveChanges]);

    const updateDropdownValue = useCallback((key: keyof DocumentMeta, dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(dropdownValue)) {
            value = parseInt((dropdownValue as DropdownOption).value);
            updateMetaField(key, value);
        }
    }, [updateMetaField]);

    const updateDescription = useCallback((e: React.ChangeEvent<HTMLInputElement>) => updateMetaField('documentDescription', e.target.value), [updateMetaField]);
    const setLinkedEntityA = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => updateDropdownValue('entityA', dropdownValue), [updateDropdownValue]);
    const setLinkedEntityB = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => updateDropdownValue('entityB', dropdownValue), [updateDropdownValue]);

    return (
        <DatasetModal
            isOpen
            closeModal={closeModal}
            topOffset={`${DATASET_INSTANCE_HEADER + NAV_BAR + ANALYSIS_MARGIN + TIMELINE_WRAPPER + ANALYSIS_PADDING}px`}
        >
            <div className={styles.editMetadataWrapper}>
                <ModalHeader label='Edit Document Metadata' icon={Document} />
                <div className={styles.contentWrapper}>
                    <Text
                        value={documentDescription}
                        onChange={updateDescription}
                        testId='document-meta-description'
                        label='Name'
                    />
                    <div className={styles.entityDropdown}>
                        <Dropdown
                            disabled={!entityAOptions.length}
                            value={uploadEntityA}
                            options={entityAOptions}
                            onChange={setLinkedEntityA}
                            testId='document-meta-entity-a'
                            placeholder={entityAOptions.length ? 'Party A...' : 'No entities found'}
                            menuPortalTarget={document.body}
                            isClearable={false}
                            label='Party A'
                        />
                    </div>
                    <div className={styles.entityDropdown}>
                        <Dropdown
                            disabled={!entityBOptions.length}
                            value={uploadEntityB}
                            options={entityBOptions}
                            onChange={setLinkedEntityB}
                            testId='document-meta-entity-b'
                            placeholder={entityBOptions.length ? 'Party B...' : 'No entities found'}
                            menuPortalTarget={document.body}
                            isClearable={false}
                            label='Party B'
                        />
                    </div>
                    <div className={styles.dateWrapper} data-testid='document-date-wrapper'>
                        <div data-testid='date-of-execution-wrapper' className={styles.executedDateWrapper}>
                            <div className={styles.executedDateHeader}>Date of Execution</div>
                            <div className={styles.dateInput}>
                                <DatePicker
                                    value={dateValue}
                                    onChange={setExecutedDate}
                                    testId='document-meta-executed-date'
                                    maxDate={maxDate}
                                    minDate={minDate}
                                />
                            </div>
                        </div>
                        <div data-testid='dated-as-of-wrapper' className={styles.datedAsOfWrapper}>
                            <div className={styles.datedAsOfHeader}>Dated as of</div>
                            <div className={styles.dateInput}>
                                <DatePicker
                                    value={datedAsOfValue}
                                    onChange={setDatedAsOf}
                                    testId='document-meta-dated-as-of'
                                    maxDate={maxDate}
                                    minDate={minDatedAsOf}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <SpaceBetweenButtonWrapper leftButtons={leftButtons} rightButtons={rightButtons} />
            </div>
        </DatasetModal>
    );
};
